import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Leaf, Eye, ChefHat, Clock, Flame, Filter, Search, X, Award, Utensils } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Footer from '../components/Footer';

interface MenuCategory {
    id: string;
    name: string;
    description: string;
    is_active: boolean;
    sort_order: number;
    language_code: string;
    created_at?: string;
    updated_at?: string;
}

interface MenuItem {
    id: string;
    name: string;
    description: string;
    price: number;
    category: string;
    image: string;
    is_vegetarian: boolean;
    is_spicy: boolean;
    is_available: boolean;
}

const Menu = () => {
    const { currentLanguage, t } = useLanguage();
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [menuCategories, setMenuCategories] = useState<MenuCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [filters, setFilters] = useState({
        vegetarian: false,
        spicy: false,
        priceRange: 'all' // 'all', 'under-15', '15-20', 'over-20'
    });
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    // Récupérer les catégories depuis l'API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                console.log('Fetching categories from API...', { currentLanguage });
                const response = await axios.get(`/api/v1/menu-categories?lang=${currentLanguage}`);
                console.log('Categories API response:', response.data);

                // Filtrer les catégories actives pour la langue courante
                const activeCategories = response.data
                    .filter((cat: MenuCategory) => cat.is_active)
                    .sort((a: MenuCategory, b: MenuCategory) => a.sort_order - b.sort_order);

                setMenuCategories(activeCategories);
            } catch (error) {
                console.error('Failed to fetch categories:', error);
                setMenuCategories([]);
            } finally {
                setCategoriesLoading(false);
            }
        };

        fetchCategories();
    }, [currentLanguage]);

    // Récupérer les items du menu selon la catégorie sélectionnée
    useEffect(() => {
        const fetchMenu = async () => {
            try {
                setLoading(true);
                console.log('Fetching menu from API...', { selectedCategory, currentLanguage });

                let url = '/api/v1/menu';
                const params = new URLSearchParams();
                params.append('lang', currentLanguage);

                if (selectedCategory !== 'all') {
                    params.append('category_id', selectedCategory);
                }

                const fullUrl = `${url}?${params.toString()}`;
                console.log('Menu API URL:', fullUrl);
                const response = await axios.get(fullUrl);
                console.log('Menu API response:', response.data);
                setMenuItems(response.data || []);
            } catch (error) {
                console.error('Failed to fetch menu:', error);
                setMenuItems([]);
            } finally {
                setLoading(false);
            }
        };

        // Ne récupérer le menu que si les catégories sont chargées
        if (!categoriesLoading) {
            fetchMenu();
        }
    }, [selectedCategory, categoriesLoading, currentLanguage]);

    // Filtrage avancé des plats
    const filteredItems = menuItems.filter(item => {
        // Filtre par recherche
        if (searchQuery && !item.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !item.description.toLowerCase().includes(searchQuery.toLowerCase())) {
            return false;
        }

        // Filtre végétarien
        if (filters.vegetarian && !item.is_vegetarian) {
            return false;
        }

        // Filtre épicé
        if (filters.spicy && !item.is_spicy) {
            return false;
        }

        // Filtre par prix
        if (filters.priceRange !== 'all') {
            switch (filters.priceRange) {
                case 'under-15':
                    if (item.price >= 15) return false;
                    break;
                case '15-20':
                    if (item.price < 15 || item.price > 20) return false;
                    break;
                case 'over-20':
                    if (item.price <= 20) return false;
                    break;
            }
        }

        return true;
    });

    console.log('Selected category:', selectedCategory);
    console.log('Total items:', menuItems.length);
    console.log('Categories count:', menuCategories.length);

    const getDietaryIcons = (item: MenuItem) => {
        const icons = [];
        if (item.is_vegetarian) {
            icons.push({ icon: Leaf, label: t('menu.vegetarian'), color: 'text-green-600' });
        }
        if (item.is_spicy) {
            icons.push({ icon: Flame, label: t('menu.spicy'), color: 'text-red-600' });
        }
        return icons;
    };

    if (categoriesLoading) {
        return (
            <div className="pt-16 lg:pt-20 section-padding">
                <div className="container mx-auto max-w-7xl container-padding">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">{t('menu.loadingCategories')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-16 lg:pt-20">
            {/* Enhanced Header */}
            <section className="relative section-padding bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-2xl"></div>
                    <div className="absolute bottom-20 right-10 w-80 h-80 bg-accent/5 rounded-full blur-2xl"></div>
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-secondary/3 rounded-full blur-2xl"></div>

                    {/* Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="w-full h-full" style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2030/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                        }}></div>
                    </div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding text-center">
                    <motion.div
                        ref={ref}
                        initial={{ opacity: 0, y: 50 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8 }}
                    >
                        {/* Badge */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={inView ? { opacity: 1, scale: 1 } : {}}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="inline-flex items-center space-x-2 bg-primary/10 text-primary-400 px-6 py-3 rounded-full text-sm font-medium mb-8"
                        >
                            <ChefHat className="w-5 h-5" />
                            <span>{t('menu.internationalCuisine')}</span>
                        </motion.div>

                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 text-white leading-tight">
                            {t('menu.title')} <span className="text-gradient">{t('menu.culinaryDelights')}</span>
                        </h1>
                        <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light mb-12">
                            {t('menu.curriesDescription')}
                        </p>

                        {/* Menu Stats */}
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="flex justify-center items-center space-x-8 flex-wrap gap-4"
                        >
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Utensils className="w-5 h-5 text-primary-400" />
                                <span className="text-sm text-gray-300 font-medium">{menuItems.length} {t('menu.dishes')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Leaf className="w-5 h-5 text-green-400" />
                                <span className="text-sm text-gray-300 font-medium">{menuItems.filter(item => item.is_vegetarian).length} {t('menu.vegetarian')}</span>
                            </div>
                            <div className="flex items-center space-x-2 bg-white/5 px-4 py-2 rounded-full">
                                <Award className="w-5 h-5 text-yellow-500" />
                                <span className="text-sm text-gray-300 font-medium">{t('menu.chefSelection')}</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* Enhanced Menu Content */}
            <section className="relative section-padding bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
                {/* Background Decorations */}
                <div className="absolute inset-0">
                    <div className="absolute top-32 left-20 w-64 h-64 bg-primary/3 rounded-full blur-xl"></div>
                    <div className="absolute bottom-32 right-20 w-80 h-80 bg-accent/3 rounded-full blur-xl"></div>
                </div>

                <div className="relative container mx-auto max-w-7xl container-padding">
                    {/* Search and Filters Bar */}
                    <div className="mb-12 bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-xl border border-gray-100 dark:border-gray-700">
                        <div className="flex flex-col lg:flex-row gap-6 items-center">
                            {/* Search Bar */}
                            <div className="flex-1 w-full lg:w-auto">
                                <div className="relative">
                                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder={t('menu.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-300"
                                    />
                                </div>
                            </div>

                            {/* Filter Toggle */}
                            <button
                                onClick={() => setShowFilters(!showFilters)}
                                className={`inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 ${showFilters
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Filter className="w-5 h-5" />
                                <span>{t('menu.filters')}</span>
                            </button>


                        </div>

                        {/* Advanced Filters */}
                        {showFilters && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {/* Dietary Filters */}
                                    <div>
                                        <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">{t('menu.dietary')}</h4>
                                        <div className="space-y-2">
                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.vegetarian}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, vegetarian: e.target.checked }))}
                                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                />
                                                <Leaf className="w-4 h-4 text-green-600" />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{t('menu.vegetarian')}</span>
                                            </label>
                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={filters.spicy}
                                                    onChange={(e) => setFilters(prev => ({ ...prev, spicy: e.target.checked }))}
                                                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                                />
                                                <Flame className="w-4 h-4 text-red-600" />
                                                <span className="text-sm text-gray-700 dark:text-gray-300">{t('menu.spicy')}</span>
                                            </label>
                                        </div>
                                    </div>

                                    {/* Price Range */}
                                    <div>
                                        <h4 className="font-semibold mb-3 text-gray-900 dark:text-white">{t('menu.priceRange')}</h4>
                                        <select
                                            value={filters.priceRange}
                                            onChange={(e) => setFilters(prev => ({ ...prev, priceRange: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                        >
                                            <option value="all">{t('menu.allPrices')}</option>
                                            <option value="under-15">{t('menu.under15')}</option>
                                            <option value="15-20">{t('menu.15to20')}</option>
                                            <option value="over-20">{t('menu.over20')}</option>
                                        </select>
                                    </div>

                                    {/* Reset Filters */}
                                    <div className="flex items-end">
                                        <button
                                            onClick={() => {
                                                setFilters({ vegetarian: false, spicy: false, priceRange: 'all' });
                                                setSearchQuery('');
                                                setSelectedCategory('all');
                                            }}
                                            className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
                                        >
                                            {t('menu.reset')}
                                        </button>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </div>

                    {/* Enhanced Category Filters */}
                    <div className="mb-12">
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">{t('menu.exploreCategories')}</h3>
                        <div className="flex flex-wrap justify-center gap-4">
                            {/* Bouton "Tous les plats" */}
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setSelectedCategory('all')}
                                className={`inline-flex items-center space-x-2 px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${selectedCategory === 'all'
                                    ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
                                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                                    }`}
                            >
                                <span>{t('menu.allDishes')}</span>
                                <span className="bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 px-2 py-1 rounded-full text-xs font-bold">
                                    {menuItems.length}
                                </span>
                            </motion.button>

                            {/* Boutons de catégories dynamiques */}
                            {menuCategories.map((category) => (
                                <motion.button
                                    key={category.id}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => {
                                        console.log('Catégorie sélectionnée:', category);
                                        setSelectedCategory(category.id);
                                    }}
                                    className={`inline-flex items-center px-6 py-4 rounded-2xl font-semibold transition-all duration-300 ${selectedCategory === category.id
                                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg'
                                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
                                        }`}
                                    title={category.description}
                                >
                                    <span>{category.name}</span>
                                </motion.button>
                            ))}
                        </div>
                    </div>



                    {/* État de chargement pour les items */}
                    {loading && (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">{t('menu.loadingItems')}</p>
                        </div>
                    )}

                    {/* Enhanced Menu Grid */}
                    {!loading && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredItems.map((item, index) => (
                                <motion.div
                                    key={item.id}
                                    initial={{ opacity: 0, y: 50 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: index * 0.1 }}
                                    viewport={{ once: true }}
                                    className="group bg-white dark:bg-gray-800 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 hover:-translate-y-2"
                                >
                                    {/* Enhanced Image */}
                                    <div className="relative h-64 overflow-hidden">
                                        <img
                                            src={item.image}
                                            alt={item.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                            onError={(e) => {
                                                e.currentTarget.src = `https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=400&h=300&fit=crop&auto=format`;
                                            }}
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                                        {/* Dietary Icons */}
                                        <div className="absolute top-4 left-4 flex gap-2">
                                            {getDietaryIcons(item).map((dietary, idx) => (
                                                <motion.div
                                                    key={idx}
                                                    whileHover={{ scale: 1.1 }}
                                                    className="w-10 h-10 bg-white/95 rounded-full flex items-center justify-center shadow-lg"
                                                    title={dietary.label}
                                                >
                                                    <dietary.icon className={`w-5 h-5 ${dietary.color}`} />
                                                </motion.div>
                                            ))}
                                        </div>

                                        {/* Price Badge */}
                                        <div className="absolute top-4 right-4">
                                            <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 rounded-2xl font-bold shadow-lg">
                                                ${item.price.toFixed(2)}
                                            </div>
                                        </div>

                                        {/* Chef's Recommendation - basé sur un flag ou prix élevé */}
                                        {item.price > 20 && (
                                            <div className="absolute bottom-4 left-4">
                                                <div className="bg-yellow-500 text-black px-3 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                                                    <Award className="w-3 h-3" />
                                                    <span>{t('menu.chefSelection')}</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                                            <motion.button
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                                onClick={() => setSelectedItem(item)}
                                                className="bg-white text-gray-900 px-6 py-3 rounded-xl font-semibold flex items-center space-x-2 shadow-lg"
                                            >
                                                <Eye className="w-5 h-5" />
                                                <span>{t('menu.viewDetails')}</span>
                                            </motion.button>
                                        </div>
                                    </div>

                                    {/* Enhanced Content */}
                                    <div className="p-6">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex-1">
                                                <h3 className="text-xl font-serif font-bold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 transition-colors">
                                                    {item.name}
                                                </h3>
                                                <div className="flex items-center space-x-2 mb-3">
                                                    {getDietaryIcons(item).map((dietary, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full text-xs font-medium"
                                                        >
                                                            <dietary.icon className={`w-3 h-3 ${dietary.color}`} />
                                                            <span className="text-gray-700 dark:text-gray-300">{dietary.label}</span>
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed mb-4 line-clamp-3">
                                            {item.description}
                                        </p>


                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}

                    {/* Enhanced Empty State */}
                    {!loading && filteredItems.length === 0 && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center py-20"
                        >
                            <div className="max-w-md mx-auto">
                                <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <ChefHat className="w-12 h-12 text-gray-400" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{t('menu.noItems')}</h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-8">
                                    {t('menu.noItemsDescription')}
                                </p>
                                <button
                                    onClick={() => {
                                        setFilters({ vegetarian: false, spicy: false, priceRange: 'all' });
                                        setSearchQuery('');
                                        setSelectedCategory('all');
                                    }}
                                    className="bg-gradient-to-r from-primary-500 to-accent-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-primary-600 hover:to-accent-600 transition-all duration-300"
                                >
                                    {t('menu.resetFilters')}
                                </button>
                            </div>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* Enhanced Item Modal */}
            {selectedItem && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 50 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 50 }}
                        className="bg-white dark:bg-gray-800 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-700"
                    >
                        {/* Enhanced Header */}
                        <div className="relative h-80">
                            <img
                                src={selectedItem.image}
                                alt={selectedItem.name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                    e.currentTarget.src = `https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=800&h=400&fit=crop&auto=format`;
                                }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />

                            {/* Close Button */}
                            <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => setSelectedItem(null)}
                                className="absolute top-6 right-6 w-12 h-12 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-300"
                            >
                                <X className="w-6 h-6 text-white" />
                            </motion.button>

                            {/* Dietary Icons */}
                            <div className="absolute top-6 left-6 flex gap-3">
                                {getDietaryIcons(selectedItem).map((dietary, idx) => (
                                    <div
                                        key={idx}
                                        className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center"
                                        title={dietary.label}
                                    >
                                        <dietary.icon className={`w-6 h-6 ${dietary.color}`} />
                                    </div>
                                ))}
                            </div>

                            {/* Price and Rating */}
                            <div className="absolute bottom-6 left-6 right-6">
                                <div className="flex items-end justify-between">
                                    <div>
                                        <h2 className="text-3xl font-serif font-bold text-white mb-2">
                                            {selectedItem.name}
                                        </h2>

                                    </div>
                                    <div className="text-right">
                                        <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white px-6 py-3 rounded-2xl">
                                            <span className="text-2xl font-bold">${selectedItem.price.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Enhanced Content */}
                        <div className="p-8">
                            {/* Description */}
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center space-x-2">
                                    <ChefHat className="w-5 h-5 text-primary-600" />
                                    <span>Description</span>
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {selectedItem.description}
                                </p>
                            </div>

                            {/* Dietary Information */}
                            <div className="mb-8">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Informations</h3>
                                <div className="flex flex-wrap gap-3">
                                    {getDietaryIcons(selectedItem).map((dietary, idx) => (
                                        <span
                                            key={idx}
                                            className="inline-flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-xl font-medium"
                                        >
                                            <dietary.icon className={`w-4 h-4 ${dietary.color}`} />
                                            <span className="text-gray-700 dark:text-gray-300">{dietary.label}</span>
                                        </span>
                                    ))}
                                    <span className="inline-flex items-center space-x-2 bg-green-100 dark:bg-green-900/30 px-4 py-2 rounded-xl font-medium">
                                        <Clock className="w-4 h-4 text-green-600" />
                                        <span className="text-green-700 dark:text-green-400">Temps: 15-20 min</span>
                                    </span>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-center">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setSelectedItem(null)}
                                    className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-4 px-8 rounded-2xl font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                >
                                    {t('menu.close')}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Menu;
